import { useMemo, useState } from 'react';
import { TextareaSimple } from 'src/v2/commonComponents/TextareaSimple';
import { PaperPlaneRight } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import { getEditorMarkdown, validatePrompt } from '../../utils';
import classNames from 'classnames';
import {
  ArtifactType,
  EditorGraphInput,
  ProgrammingLanguageOptions,
} from 'src/types';
import {
  useEditorState,
  useRunEditorStream,
  useCodeAutoSave,
  useGetUser,
} from 'src/hooks';
import styles from './PromptInputBox.module.scss';
import { getHumanMessage } from 'src/utils';
import { useDocumentAutoSave } from 'src/hooks/editorHooks/useDocumentAutoSave';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

export const PromptInputBox = () => {
  const [value, setValue] = useState<string>('');
  const [errorText, setErrorText] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const { editorType, codeEditorData } = useEditorState();
  const { language } = codeEditorData ?? {};

  const { isFreeTier } = useGetUser();
  const { onRunEditorStream } = useRunEditorStream();
  const { onDocumentAutoSave } = useDocumentAutoSave();
  const { onCodeAutoSave } = useCodeAutoSave();
  const [editor] = useLexicalComposerContext();

  const clearedValue = useMemo(() => value.trim(), [value]);
  const isSubmitDisabled = useMemo(
    () => !clearedValue || isLoading,
    [clearedValue, isLoading],
  );

  const handleChange = (value: string) => {
    if (errorText) {
      setErrorText('');
    }
    const { validatedValue, isError, wordLimit } = validatePrompt(value);
    setValue(validatedValue);

    if (isError) {
      const errorText = `Word limit exceeded. The maximum allowed is ${new Intl.NumberFormat('en-US').format(wordLimit)}.`;
      setErrorText(errorText);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }

    setLoading(true);
    setValue('');

    if (editorType === ArtifactType.CODE) {
      await onCodeAutoSave(codeEditorData?.content || '');
    } else {
      const fullMarkdown = getEditorMarkdown(editor);
      await onDocumentAutoSave(fullMarkdown);
    }

    const userMessage = getHumanMessage(value);

    const input: EditorGraphInput = {
      userMessage,
      ...(editorType === ArtifactType.CODE && {
        currentCodingLanguage: language as ProgrammingLanguageOptions,
      }),
    };

    await onRunEditorStream(input);

    setLoading(false);
  };

  return (
    <div className={styles.root}>
      <div
        className={classNames(styles.container, {
          [styles.error]: !!errorText,
        })}
      >
        <TextareaSimple
          className={styles.textarea}
          value={value}
          onChange={handleChange}
          placeholder="Prompt to edit the document"
          maxRows={4}
          minRows={2}
          onSubmit={handleSubmit}
          disabled={isFreeTier}
          tooltipContent={
            isFreeTier ? 'Available with subscription' : undefined
          }
        />

        <Button
          disabled={isSubmitDisabled}
          shape="round"
          color={clearedValue ? 'primary' : 'transparent'}
          onClick={handleSubmit}
        >
          <PaperPlaneRight
            weight={clearedValue ? 'fill' : 'regular'}
            size={SVG_SIZE_M}
          />
        </Button>
      </div>

      {errorText && <p className={styles.errorText}>{errorText}</p>}
    </div>
  );
};
